import { supertypeClass, property } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import { Assumptions } from '../static/Assumptions';
import { Customer } from './Customer';
import { Utils } from '../Utils';
import { Location } from '../interfaces/Location';
import { Address as GQLAddress, State } from '@hit/hit-api-client';

export type AddressHolder = {
  title: string;
  address: Address;
  name: string;
  prop: string;
};

@supertypeClass({ toClient: true, toServer: true })
export class Address extends Everything implements GQLAddress {
  @property()
  bucket: string;

  @property({ values: { '1': 'Home', '2': 'Business', '17': 'Mailing', '99': 'Legal', '2147483647': 'Other' } })
  type: string = '1';

  @property({ rule: ['text', 'required'], length: 35 })
  street: string;

  @property({ rule: ['text'], length: 35 })
  line1: string;

  @property({ rule: ['text'], length: 35 })
  line2: string;

  @property()
  line3: string;

  @property({ rule: ['geoName'], length: 40 })
  city: string;

  @property({ rule: ['required', 'zipcode'] })
  zip: string;

  @property({ values: Assumptions.stateValues })
  state: string;

  @property()
  timezone: string;

  @property()
  latitude: number;

  @property()
  longitude: number;

  @property({ rule: ['required', 'text'], values: Assumptions.countryValues })
  country: string = 'US';

  @property({ getType: () => Customer })
  customer: Customer;

  @property({ isLocal: true })
  zipSet;

  @property({ isLocal: true })
  prefillOwnerAddressWithInsured: boolean;

  constructor() {
    super();
  }

  static new(customer?) {
    return new Address().init(customer);
  }

  init(customer?) {
    if (customer) {
      this.customer = customer;
      this.customer.addresses.push(this);
    }
    return this;
  }

  zipTrigger() {
    if (typeof this.zipSet === 'function') {
      return this.zipSet(this.zip);
    }
  }

  countryTrigger() {
    if (!this.country) {
      this.country = 'US';
    }
  }

  setLocation(location: Location) {
    this.state = location.stateCode;
    this.city = location.city;
    this.latitude = location.lat;
    this.longitude = location.lng;
    this.timezone = location.timezone;
  }

  getOneLineValue() {
    if (!this.street && !this.line1 && !this.city && !this.state && !this.zip) {
      return '';
    }
    return (
      (this.street ? this.street + ', ' : '') +
      (this.line1 ? this.line1 + ', ' : '') +
      (this.city ? this.city : '') +
      (this.state ? ', ' + this.state : '') +
      ' ' +
      (this.zip ? this.zip : '')
    );
  }

  getOneLineValueWithCountry() {
    return this.getLine1() + this.getLine2WithCountry();
  }

  getOneLineValueOmittingMissingFields(separator) {
    return Utils.sparseArraytoString([this.street, this.line1, this.city, this.state, this.zip], separator);
  }

  getLine1() {
    return (this.street ? this.street + ', ' : '') + (this.line1 ? this.line1 + ', ' : '');
  }

  getLine2() {
    return (this.city ? this.city : '') + (this.state ? ', ' + this.state : '') + ' ' + (this.zip ? this.zip : '');
  }

  getLine2WithCountry() {
    return (
      (this.city ? this.city : '') +
      (this.state ? ', ' + this.state : '') +
      (this.zip ? ', ' + this.country : '') +
      ' ' +
      (this.zip ? this.zip : '')
    );
  }

  getListValue() {
    return (
      (this.street ? this.street + ', ' : '') + (this.city ? this.city : '') + ', ' + (this.state ? this.state : '')
    );
  }

  toString() {
    if (!(this.street && this.city && this.state && this.zip)) {
      return '';
    }

    return this.getListValue();
  }

  getChooserDisplay() {
    return this.toString();
  }

  isHome() {
    return this.type === '1';
  }

  reset() {
    this.street = this.line1 = this.line2 = this.city = this.state = this.zip = this.timezone = null;
  }
}
